/* src/pages/About.js */
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useForm, ValidationError } from "@formspree/react";
import ModalPortal from "../components/ModalPortal";
import Seo from "../components/seo";
import { currentLanguage } from "../utils/locale";

/* =========================================================
   THEME TOKENS (tweak here to re-skin fast)
========================================================= */
const WRAP = "px-[1vw]";              // global side padding (~5%)
const MAXW = "max-w-10xl mx-auto";     // page max width
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " + BORDER_SOFT + " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Color ribbons & gradients */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%) 
`;

/* Panels (non-glass, textile-like) */
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.4),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";
const SILK_PLUM =
  "bg-[linear-gradient(135deg,rgba(56,18,40,0.8),rgba(12,45,79,0.2))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(75%_55%_at_15%_0%,rgba(140,59,90,0.3),transparent_100%)] before:content-['']";

/* Accent lines */
const ACCENT_LINE = "bg-gradient-to-r from-[#39C3D4] via-[#0FA7C0] to-[#8C3B5A]";

/* =========================================================
   SEO helpers
========================================================= */
const pageUrl = "https://tarotfate.com/about";
const siteUrl = "https://tarotfate.com";
const ogImage = "https://tarotfate.com/icons/og-cover.jpg";

const metaDescription =
  "Learn the story and philosophy behind TarotFate, a free tarot site built for reflection, card study and practical emotional clarity.";

 // Page-specific keywords (About)
const aboutKeywords = `
about tarotfate, emotion-based tarot, tarot philosophy, ethical tarot, how to use tarot,
creator story, site mission, mindful tarot, reflective tarot, contact tarotfate, donate tarotfate
`.replace(/\s+/g, " ").trim();


const schemaGraph = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${siteUrl}#org`,
      name: "TarotFate",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: "https://tarotfate.com/icons/logo.webp",
        width: 512,
        height: 512
      }
    },
    {
      "@type": "WebSite",
      "@id": `${siteUrl}#website`,
      url: siteUrl,
      name: "TarotFate",
      publisher: { "@id": `${siteUrl}#org` },
      inLanguage: "en"
    },
    {
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: "About TarotFate | Emotion-Based Tarot & Our Philosophy",
      description: metaDescription,
      isPartOf: { "@id": `${siteUrl}#website` },
      primaryImageOfPage: { "@id": `${pageUrl}#image` },
      breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
      inLanguage: "en"
    },
    {
      "@type": "ImageObject",
      "@id": `${pageUrl}#image`,
      url: ogImage,
      width: 1200,
      height: 630,
      caption: "TarotFate cover"
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "About", item: pageUrl }
      ]
    },
    {
      "@type": "FAQPage",
      "@id": `${pageUrl}#faq`,
      mainEntity: [
        {
          "@type": "Question",
          name: "Is TarotFate predictive?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "No. TarotFate emphasizes reflection, not fortune telling. Cards mirror your present so you can choose grounded next steps."
          }
        },
        {
          "@type": "Question",
          name: "Which spread should I choose?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "1 card = quick pulse; 3 cards = past-present-future; 5 cards = deeper map covering root, shadow, shift, strength, next step."
          }
        },
        {
          "@type": "Question",
          name: "Do I need to type a whisper or question?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "For Other, a short description is required. Otherwise it is optional but helps personalize the reading."
          }
        },
        {
          "@type": "Question",
          name: "Do you store my data or readings?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "No accounts or saved readings. Donations are handled securely by PayPal."
          }
        },
        {
          "@type": "Question",
          name: "Can I explore card meanings here?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Yes, Major Arcana and the suits Wands, Cups, Swords, Pentacles include upright & reversed meanings."
          }
        }
      ]
    }
  ]
};

export default function About() {
  const isSpanish = currentLanguage() === "es";
  /* =========================================
     Existing popup & donate plumbing (kept)
  ========================================= */
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [showDonatePopup, setShowDonatePopup] = useState(false);
  const [showDonation, setShowDonation] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [contactMounted, setContactMounted] = useState(false);
  const [donateMounted, setDonateMounted] = useState(false);
  const [formState, handleSubmit] = useForm("xvgqwqll");

  const donationAmounts = [5, 10, 20, 50, 100];

  const [clickOrigin, setClickOrigin] = useState({
    x: typeof window !== "undefined" ? window.innerWidth / 2 : 0,
    y: typeof window !== "undefined" ? window.innerHeight / 2 : 0
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (showContactPopup) {
          setContactMounted(false);
          setTimeout(() => setShowContactPopup(false), 180);
        }
        if (showDonatePopup) {
          setDonateMounted(false);
          setTimeout(() => setShowDonatePopup(false), 180);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showContactPopup, showDonatePopup]);

  const recordClickCenter = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setClickOrigin({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
  };

  if (isSpanish) {
    const spanishDescription =
      "Conoce la historia y filosofia de TarotFate, un sitio gratuito de tarot para reflexion, estudio de cartas y claridad emocional practica.";
    const spanishFaq = [
      {
        q: "TarotFate predice el futuro?",
        a: "No. TarotFate se enfoca en reflexion, no en adivinacion. Las cartas ayudan a mirar lo que estas viviendo para elegir pasos mas conscientes.",
      },
      {
        q: "Que tirada deberia elegir?",
        a: "Una carta sirve para una senal rapida. Tres cartas dan pasado, presente y futuro. Cinco cartas ayudan a explorar raiz, sombra, cambio, fortaleza y siguiente paso.",
      },
      {
        q: "Necesito escribir una pregunta?",
        a: "Solo es necesario si eliges Otra emocion. En los demas casos es opcional, pero ayuda a que la lectura se sienta mas cercana a tu contexto.",
      },
      {
        q: "Guardan mis datos o mis lecturas?",
        a: "No hay cuentas ni lecturas guardadas. Las donaciones, si las haces, se procesan de forma segura por PayPal.",
      },
      {
        q: "Puedo estudiar los significados de las cartas aqui?",
        a: "Si. Puedes explorar Arcanos Mayores, Bastos, Copas, Espadas y Oros con significados al derecho e invertidos.",
      },
    ];

    return (
      <div className={`${FONT} text-white min-h-[100svh] relative overflow-hidden`}>
        <div className="absolute inset-0 -z-10" style={{ background: CANVAS_BG }} />
        <Seo
          title="Sobre TarotFate | Tarot emocional y filosofia"
          description={spanishDescription}
          url={`${siteUrl}/es/about`}
          image={ogImage}
          keywords="sobre tarotfate, tarot emocional, tarot etico, filosofia del tarot, tarot reflexivo"
        />

        <div className="pointer-events-none absolute inset-0 transform-gpu">
          <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[80px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
          <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[70px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
        </div>

        <section className={`pt-6 md:pt-6 ${WRAP} ${MAXW} text-center`}>
          <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">Sobre TarotFate</h1>
          <div className="flex items-center justify-center mb-1">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
            <span className="px-4 text-[#f3eaff]/100">✦</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          </div>
          <p className={`mt-2 ${TEXT} text-base md:text-[18px]`}>
            Tarot guiado por emociones para reflexionar, no para predecir.
          </p>
        </section>

        <section className={`mt-8 ${WRAP} ${MAXW}`}>
          <div className={`${PANEL_BASE} ${SILK_TURQ} p-2 sm:p-6 md:p-6 opacity-100`}>
            <div className={`relative z-[1] ${TEXT} text-[17px] md:text-[18px] leading-[1.8]`}>
              <p>
                <strong className="text-white">TarotFate.com</strong> existe para esos momentos tranquilos en que la vida se siente demasiado ruidosa. Cuando algo se mueve dentro de ti, pero todavia no tienes palabras para nombrarlo.
              </p>
              <p className="mt-5">
                TarotFate no esta aqui para prometer el futuro ni convertir la incertidumbre en miedo. Esta aqui para ayudarte a quedarte con una emocion el tiempo suficiente para escuchar lo que puede estar pidiendo de ti.
              </p>
              <p className="mt-5">
                Puedes llegar con una decision, una duda, una perdida, una esperanza o simple curiosidad. Este espacio te recibe como estas: cansada, ilusionada, confundida, sensible o despierta tarde con demasiados pensamientos.
              </p>
              <p className="mt-5">
                No necesitas creer en magia para usar este sitio. Solo necesitas aceptar que tus emociones importan y que una buena pregunta puede cambiar el siguiente paso.
              </p>

              <div className="mt-8 p-4 rounded-lg bg-white/5 border border-white/10">
                <h2 className="text-lg font-semibold text-white mb-3">En TarotFate creemos que el tarot pertenece a todos.</h2>
                <p className="mt-3">
                  Muchas personas conocen el tarot a traves de lecturas caras, respuestas recicladas o mensajes que alimentan miedo. TarotFate busca otra cosa: claridad, autonomia y una relacion mas honesta con las cartas.
                </p>
                <p className="mt-4">
                  Los significados, tiradas, paginas emocionales y herramientas estan abiertos para que puedas leer, comparar y aprender a tu ritmo, sin depender de una sola respuesta.
                </p>
              </div>
            </div>
            <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          </div>
        </section>

        <section className={`mt-4 ${WRAP} ${MAXW}`}>
          <div className={`${PANEL_BASE} p-6 sm:p-8 md:p-10 bg-[linear-gradient(135deg,rgba(18,44,78,0.88),rgba(9,27,46,0.4),rgba(130,30,60,0.5))]`}>
            <div className="text-center">
              <div className="inline-flex items-center gap-3 uppercase tracking-[0.22em] text-base sm:text-base font-medium text-white/85">
                <span>Como usar TarotFate</span>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
              <StepCard step="1" title="Nombra la emocion">Elige la emocion que mas fuerte habla hoy, o usa Otra emocion para escribir una frase breve.</StepCard>
              <StepCard step="2" title="Elige una tirada">Una carta para una senal rapida. Tres para contexto. Cinco para una mirada mas profunda.</StepCard>
              <StepCard step="3" title="Lee con calma">Considera el significado al derecho e invertido y quedate con lo que encaja con tu realidad.</StepCard>
              <StepCard step="4" title="Une el mensaje">Escribe una frase: el mensaje para mi es... Luego elige un paso amable y concreto.</StepCard>
              <StepCard step="5" title="Vuelve con cuidado">Si sigues sin claridad, descansa y vuelve despues. No uses el tarot desde la urgencia.</StepCard>
              <div className="rounded-xl border border-white/1 bg-white/5 p-4">
                <p className="uppercase tracking-widest text-[11px] text-white/80">Explorar</p>
                <h3 className="mt-1 text-white font-semibold">Significados de cartas</h3>
                <p className={`${TEXT} text-[15.5px] mt-1`}>Profundiza con Arcanos Mayores y Menores.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to="/es/tarot/major-arcana" className="tf-outline-button">Arcanos Mayores</Link>
                  <Link to="/es/tarot/wands" className="tf-outline-button">Bastos</Link>
                  <Link to="/es/tarot/cups" className="tf-outline-button">Copas</Link>
                  <Link to="/es/tarot/swords" className="tf-outline-button">Espadas</Link>
                  <Link to="/es/tarot/pentacles" className="tf-outline-button">Oros</Link>
                </div>
              </div>
            </div>
            <div className="mt-7 text-center">
              <Link to="/es" className="tf-outline-button">Empezar una lectura</Link>
            </div>
          </div>
        </section>

        <section className={`mt-10 ${WRAP} ${MAXW}`}>
          <div className="flex justify-center items-center gap-6 sm:gap-8">
            <Link to="/es/contact" className="px-6 py-3 rounded-full border border-white/35 bg-white/10 hover:bg-white/20 transition shadow-[0_6px_24px_rgba(0,0,0,0.35)]">
              Contacto
            </Link>
            <a
              href="https://www.paypal.me/tarotfatecom"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 rounded-full border border-white/35 bg-white/10 hover:bg-white/20 transition shadow-[0_6px_24px_rgba(0,0,0,0.35)]"
            >
              Donar
            </a>
          </div>
        </section>

        <section id="faq" className={`mt-12 mb-16 ${WRAP} ${MAXW}`}>
          <div className="text-center">
            <h2 className="text-base sm:text-lg font-medium">Preguntas frecuentes</h2>
          </div>
          <div className="mt-6 rounded-sm border border-white/35 bg-white/5 divide-y divide-white/25">
            {spanishFaq.map(({ q, a }, i) => (
              <details key={i} className="group">
                <summary className="cursor-pointer list-none px-4 md:px-6 py-3 text-sm sm:text-sm text-white/95 font-medium flex items-start justify-between gap-6 after:content-['+'] after:ml-4 after:text-white/80 after:text-xl after:leading-none group-open:after:content-['-']">
                  <span className="pr-2">{q}</span>
                </summary>
                <div className={`${TEXT} text-sm leading-relaxed px-4 md:px-6 pb-4`}>{a}</div>
              </details>
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={`${FONT} text-white min-h-[100svh] relative overflow-hidden`}>

  <div
    className="absolute inset-0 -z-10"
    style={{ background: CANVAS_BG }}
  />

      {/* SEO (Helmet replaced by Seo component) */}
     <Seo
  title="About TarotFate | Emotion-Based Tarot & Our Philosophy"
  description={metaDescription}
  url={pageUrl}
  image={ogImage}
  schema={schemaGraph}
  keywords={aboutKeywords}
/>


      {/* Ambient auras (no dots or noise) */}
      <div className="pointer-events-none absolute inset-0 transform-gpu">
  <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[80px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
  <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[70px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
</div>

      {/* =========================
          HERO
      ========================= */}
      <section className={`pt-6 md:pt-6 ${WRAP} ${MAXW} text-center`}>
        <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">
          About TarotFate
        </h1>
        <div className="flex items-center justify-center mb-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100 text-gl drop-shadow-[0_0_0px_rgba(203,183,234,0.8)]">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`mt-2 ${TEXT} text-base md:text-[18px]`}>
          Emotion-guided tarot for reflection, not prediction.
        </p>  
      </section>

      {/* =========================
          ABOUT PANEL (silk, non-glass)
      ========================= */}
      <section className={`mt-8 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-2 sm:p-6 md:p-6 opacity-100`}>
          <div className={`relative z-[1] ${TEXT} text-[17px] md:text-[18px] leading-[1.8]`}>
            <p>
              <strong className="text-white">TarotFate.com</strong>{" "}
              is here for the quiet moments when life feels loud. The times
              when you know something is moving inside you, but you do not yet have the words for it.
            </p>
            <p className="mt-5">
              Think of this site as a pause button. It is not here to predict
              the future or turn uncertainty into fear. It is here to help you
              sit with a feeling long enough to hear what it may be asking from you.
            </p>
            <p className="mt-5">
              Maybe you are facing a decision. Maybe something feels off and you
              cannot explain it. Maybe you are simply curious. Whatever brought
              you here, TarotFate meets you as you are: tired, hopeful,
              overwhelmed or awake late with your thoughts.
            </p>
            <p className="mt-5">
              You do not have to believe in magic to use this site. You only
              need to believe that your feelings matter and that a thoughtful
              question can change the next step.
            </p>
            <p className="mt-5">
              Bring tangled thoughts. Bring silence. Bring wonder. This space
              exists to give your inner life a clear place to speak.{" "}
              <strong className="text-white">One emotion at a time</strong>.
            </p>

            {/* Manifesto block */}
            <div className="mt-8 p-4 rounded-lg bg-white/5 border border-white/10">
              <h2 className="text-lg font-semibold text-white mb-3">
                At TarotFate, we believe tarot belongs to everyone.
              </h2>
              <p className="mt-3">
                Too many people meet tarot through expensive readings, recycled
                answers or fear-based claims. That is not what this site is for.
                Tarot should help people feel steadier, not more dependent.
              </p>
              <p className="mt-4">
                TarotFate keeps the cards open, readable and free. The goal is
                reflection, not control. The point is clarity, not pressure.
              </p>
              <p className="mt-4">
                Tarot can be a useful mirror when it is handled with care. It can
                help you name patterns, test your assumptions and choose one
                grounded action.
              </p>
              <p className="mt-4">
                That is why the card meanings, spreads, emotion pages and tools
                are open to everyone. You can read, compare and learn at your own
                pace without a paywall.
              </p>
              <p className="mt-4">
                TarotFate exists for readers who want honest guidance they can
                think with, question and make their own.
              </p>
            </div>
          </div>
          {/* top edge gleam */}
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* =========================
          HOW TO USE (dense + elegant)
      ========================= */}
      <section className={`mt-4 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} p-6 sm:p-8 md:p-10 bg-[linear-gradient(135deg,rgba(18,44,78,0.88),rgba(9,27,46,0.4),rgba(130,30,60,0.5))]`}>
          <div className="text-center">
            <div className="inline-flex items-center gap-3 uppercase tracking-[0.22em] text-base sm:text-base font-medium text-white/85">
              <span className="select-none"></span>
              <span>How to Use TarotFate</span>
              <span className="select-none"></span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Col 1 */}
            <div className="space-y-4">
              <StepCard step="1" title="Set Intention">
                Name your emotion or pick <em>Other</em> and write one clear sentence about what's truly on your mind.
              </StepCard>
              <StepCard step="2" title="Choose a Spread">
                <b>1 card</b> quick pulse. <b>3 cards</b> past·present·future. <b>5 cards</b> root·shadow·shift·strength·step.
              </StepCard>
            </div>

            {/* Col 2 */}
            <div className="space-y-4">
              <StepCard step="3" title="Read Both Aspects">
                Consider <em>upright</em> and <em>reversed</em>. Pick the phrasing that accurately fits your reality.
              </StepCard>
              <StepCard step="4" title="Synthesize">
                Write one line: <em>"The message for me is..."</em> Then decide a single, gentle action you can take next.
              </StepCard>
            </div>

            {/* Col 3 */}
            <div className="space-y-4">
              <StepCard step="5" title="Revisit Mindfully">
                If you still feel unclear, step away and return later. Let the message settle before seeking again.
              </StepCard>
              <div className="rounded-xl border border-white/1 bg-white/5 p-4">
                <p className="uppercase tracking-widest text-[11px] text-white/80">Explore</p>
                <h3 className="mt-1 text-white font-semibold">Card Meanings</h3>
                <p className={`${TEXT} text-[15.5px] mt-1`}>
                  Deepen your reading with the Major & Minor Arcana.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to="/tarot/major-arcana" className="tf-outline-button">Major Arcana</Link>
                  <Link to="/tarot/wands" className="tf-outline-button">Wands</Link>
                  <Link to="/tarot/cups" className="tf-outline-button">Cups</Link>
                  <Link to="/tarot/swords" className="tf-outline-button">Swords</Link>
                  <Link to="/tarot/pentacles" className="tf-outline-button">Pentacles</Link>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-7 text-center">
            <Link
              to="/"
              className="tf-outline-button"
            >
              Start a Reading
            </Link>
          </div>
        </div>
      </section>

      {/* =========================
          CREATOR'S WHISPER
      ========================= */}
      <section
        aria-labelledby="creators-whisper"
        className={`relative mt-4 ${WRAP} ${MAXW}`}
      >
        {/* Ambient ornaments */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-10 -left-6 h-40 w-40 rounded-full blur-[90px] opacity-30
                     [background:radial-gradient(60%_60%_at_50%_50%,rgba(130,30,60,0.45),transparent)]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-8 -right-8 h-44 w-44 rounded-full blur-[100px] opacity-25
                     [background:radial-gradient(60%_60%_at_50%_50%,rgba(3,121,155,0.45),transparent)]"
        />

        <div className={`${PANEL_BASE} ${SILK_PLUM} p-6 sm:p-8 md:p-10 relative overflow-hidden`}>
          {/* Section label */}
          <div className="text-center">
            <div className="inline-flex items-center gap-3 uppercase tracking-[0.22em] text-sm sm:text-base text-white/85">
              <span aria-hidden className="select-none"></span>
              <h2 id="creators-whisper" className="text-base sm:text-base font-medium">
                Creator's Whisper
              </h2>
              <span aria-hidden className="select-none"></span>
            </div>
          </div>

          {/* Aphorisms + center sigil/divider */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-start md:items-center gap-6 md:gap-8">
            {/* Left column */}
            <div className={`${TEXT} text-sm sm:text-base leading-relaxed md:text-left`}>
              <p className="flex gap-2">
                <span aria-hidden>💫</span>
                <span>Align your steps with your purpose, the stars notice.</span>
              </p>
              <p className="mt-2 flex gap-2">
                <span aria-hidden>🌙</span>
                <span>What is steady and sacred gathers its own calm gravity.</span>
              </p>
            </div>

            {/* Center crest + line */}
            <div className="hidden md:flex flex-col items-center justify-center gap-3 px-3">
              <span className={`h-16 w-px ${ACCENT_LINE}`} />
              <span aria-hidden className="text-base">✦</span>
              <span className={`h-16 w-px ${ACCENT_LINE}`} />
            </div>

            {/* Right column */}
            <div className={`${TEXT} text-sm sm:text-base leading-relaxed md:text-left`}>
              <p className="flex gap-2">
                <span aria-hidden>🔮</span>
                <span>The mystic whispers when the soul listens and acts.</span>
              </p>
              <p className="mt-2 flex gap-2">
                <span aria-hidden>✨</span>
                <span>You are not alone. Let a small light guide the next right thing.</span>
              </p>
            </div>
          </div>

      {/* Card callout */}
<div className="mt-8 text-center">
        <p className="italic text-sm sm:text-base text-white/50">
          The creator of this website's guiding card:{" "}
          <span className="text-white/50 font-semibold">Ten of Swords</span> - endings that become awakenings.
        </p>

        <div className="mt-4 flex justify-center">
          <a
            href="/tarot-card/ten-of-swords"
            className="block w-[108px] sm:w-[116px] md:w-[124px]"
          >
            <figure>
              <img
                src="/card/ten-of-swords-tarot-card-reading-meaning.webp"
                alt="Ten of Swords tarot card"
                width="124"
                height="207"
                className="w-full h-auto rounded-lg shadow-lg"
                draggable="false"
                onContextMenu={(e) => e.preventDefault()}
              />
              <div className="mt-2 text-s sm:text-sm text-white/50">
                - rising from the ashes
              </div>
            </figure>
          </a>
        </div>
      </div>
{/* Bottom hairline */}
<div
  aria-hidden="true"
  className="absolute inset-x-0 bottom-0 h-px opacity-70
             bg-gradient-to-r from-transparent via-white/35 to-transparent"
/>
        </div>
      </section>

      {/* =========================
          CTA BUTTONS
      ========================= */}
      <section className={`mt-10 ${WRAP} ${MAXW}`}>
        <div className="flex justify-center items-center gap-6 sm:gap-8">
          <button
            onClick={(e) => {
              recordClickCenter(e);
              setShowContactPopup(true);
              requestAnimationFrame(() => setContactMounted(true));
            }}
            className="px-6 py-3 rounded-full border border-white/35 bg-white/10 hover:bg-white/20 transition shadow-[0_6px_24px_rgba(0,0,0,0.35)]"
          >
            Contact
          </button>
          <button
            onClick={(e) => {
              recordClickCenter(e);
              setShowDonatePopup(true);
              requestAnimationFrame(() => setDonateMounted(true));
            }}
            className="px-6 py-3 rounded-full border border-white/35 bg-white/10 hover:bg-white/20 transition shadow-[0_6px_24px_rgba(0,0,0,0.35)]"
          >
            Donate
          </button>
        </div>
      </section>

      {/* =========================
          FAQ - compact, left text + right "+"
      ========================= */}
      <section
        id="faq"
        className={`mt-12 mb-16 ${WRAP} ${MAXW}`}
      >
        <div className="text-center">
          <h2 className="text-base sm:text-lg font-medium">Frequently Asked Questions</h2>
          <div className="mt-2 flex justify-center">
          </div>
        </div>

        <div className="mt-6 rounded-sm border border-white/35 bg-white/5 divide-y divide-white/25">
          {[
            {
              q: "Is TarotFate predictive?",
              a:
                "No. TarotFate emphasizes reflection, not fortune telling. A reading mirrors what you're navigating so you can choose grounded next steps."
            },
            {
              q: "Which spread should I choose?",
              a:
                "Choose 1 card for a quick pulse, 3 cards for past-present-future and 5 cards for deeper mapping (root, shadow, shift, strength, next step)."
            },
            {
              q: "Do I need to type a whisper or question?",
              a:
                "For Other, a short sentence is required. Otherwise it is optional but helps the reading speak to your specific context."
            },
            {
              q: "Do you store my data or readings?",
              a:
                "No accounts and no saved readings. If you donate, PayPal securely handles payment."
            },
            {
              q: "Can I explore card meanings here?",
              a:
                "Yes - Major Arcana plus Wands, Cups, Swords, Pentacles with upright & reversed meanings for love, career and guidance."
            }
          ].map(({ q, a }, i) => (
            <details key={i} className="group">
              <summary
                className="
                  cursor-pointer list-none px-4 md:px-6 py-3
                  text-sm sm:text-sm text-white/95 font-medium
                  flex items-start justify-between gap-6
                  after:content-['+'] after:ml-4 after:text-white/80 after:text-xl after:leading-none
                  group-open:after:content-['-']
                "
              >
                <span className="pr-2">{q}</span>
              </summary>
              <div className={`${TEXT} text-sm leading-relaxed px-4 md:px-6 pb-4`}>
                {a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* =========================
          POPUPS (unchanged)
      ========================= */}
      {showContactPopup && (
        <ModalPortal>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-[1px] flex items-center justify-center z-[100]"
            onClick={() => {
              setContactMounted(false);
              setTimeout(() => setShowContactPopup(false), 180);
            }}
          >
            <div
              className={`bg-white/90 backdrop-blur-sm  p-6 rounded-lg w-full max-w-md shadow-lg relative transition duration-1 ease-out ${
                contactMounted ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
              style={{ transformOrigin: `${clickOrigin.x}px ${clickOrigin.y}px` }}
              onClick={(e) => e.stopPropagation()}
            >
              <Helmet>
                <script src="https://www.google.com/recaptcha/api.js" async defer></script>
              </Helmet>

              {formState.succeeded ? (
                <p className="text-green-600 text-center">Thank you! Your message has been sent.</p>
              ) : (
                <form onSubmit={handleSubmit}>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name *"
                    required
                    className="w-full mb-2 p-2 border border-gray-300 rounded"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email *"
                    required
                    pattern="^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"
                    className="w-full mb-2 p-2 border border-gray-300 rounded"
                  />
                  <ValidationError prefix="Email" field="email" errors={formState.errors} />
                  <textarea
                    name="message"
                    placeholder="Your Message *"
                    required
                    className="w-full mb-2 p-2 border border-gray-300 rounded h-24"
                  />
                  <ValidationError prefix="Message" field="message" errors={formState.errors} />
                  <div className="g-recaptcha mb-3" data-sitekey="6LfViJsrAAAAAL98z_NLZIl4kEpH5plzrryGL-tv" />
                  <button
                    type="submit"
                    disabled={formState.submitting}
                    className="w-full py-2 bg-[#14656a] text-white rounded hover:text-white hover:bg-[#0f3d3f] transition"
                  >
                    Send Message
                  </button>
                </form>
              )}

              <button
                onClick={() => {
                  setContactMounted(false);
                  setTimeout(() => setShowContactPopup(false), 180);
                }}
                className="mt-4 text-sm text-gray-500 hover:underline block mx-auto"
              >
                Close
              </button>
            </div>
          </div>
        </ModalPortal>
      )}

      {showDonatePopup && (
        <ModalPortal>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-[1px] flex items-center justify-center z-50"
            onClick={() => {
              setDonateMounted(false);
              setTimeout(() => setShowDonatePopup(false), 180);
            }}
          >
            <div
              className={`bg-[white]/85 backdrop-blur-sm p-6 rounded-lg w-full max-w-sm shadow-lg text-center transition duration-1 ease-out ${
                donateMounted ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
              style={{ transformOrigin: `${clickOrigin.x}px ${clickOrigin.y}px` }}
              onClick={(e) => e.stopPropagation()}
            >
              <p className="mb-4 text-sm text-[#021868]">
                If this space offered you even a moment of clarity or calm, consider a small gift to help keep the light glowing for others who may need it too.
              </p>

              <div className="mt-4 flex flex-col items-center">
                <button
                  onClick={() => setShowDonation(!showDonation)}
                  className="bg-white/80 text-[#021868] border border-[#021868] px-5 py-2 rounded-full shadow-lg hover:text-white hover:bg-[#021868] transition"
                >
                  💖 Donate via PayPal
                </button>
                {showDonation && (
                  <DonateOptions
                    donationAmounts={donationAmounts}
                    customAmount={customAmount}
                    setCustomAmount={setCustomAmount}
                  />
                )}
              </div>

              <button
                onClick={() => {
                  setDonateMounted(false);
                  setTimeout(() => setShowDonatePopup(false), 180);
                }}
                className="mt-4 text-sm text-gray-500 hover:underline block mx-auto"
              >
                Close
              </button>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}

/* =========================================================
   Subcomponents
========================================================= */
function StepCard({ step, title, children }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <p className="uppercase tracking-widest text-[11px] text-white/80">Step {step}</p>
      <h3 className="mt-1 text-white font-semibold">{title}</h3>
      <p className="text-white/90 text-[15.5px] mt-1">{children}</p>
    </div>
  );
}

function DonateOptions({ donationAmounts, customAmount, setCustomAmount }) {
  return (
    <div className="mt-4 w-full flex flex-col items-center space-y-3">
      <div className="flex flex-wrap justify-center gap-3">
        {donationAmounts.map((amount) => (
          <a
            key={amount}
            href={`https://www.paypal.me/tarotfatecom/${amount}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white border border-[#021868] text-[#021868] px-4 py-2 rounded-xl text-sm hover:text-white hover:bg-[#021868] transition"
          >
            ${amount}
          </a>
        ))}
      </div>
      <div className="text-center mt-2">
        <p className="text-xs text-[#021868] mb-1">Or enter your own amount (max $1,000)</p>
        <input
          type="text"
          inputMode="decimal"
          value={customAmount}
          onChange={(e) => {
            const v = e.target.value.trim();
            const ok = /^(\d+(\.\d{0,2})?)?$/.test(v);
            if (ok && parseFloat(v || "0") <= 1000) setCustomAmount(v);
          }}
          className="border border-[#c3b0e7] rounded px-3 py-1 w-32 text-center text-[#5e4b8b]"
          placeholder="Other ($)"
        />
        {customAmount && parseFloat(customAmount) > 0 && (
          <div className="mt-2">
            <a
              href={`https://www.paypal.me/tarotfatecom/${customAmount}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-[#021868] px-4 py-1.5 rounded-full shadow-lg hover:text-white hover:bg-[#021868] transition text-sm"
            >
              Donate ${customAmount}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

